import {
  MpfsShare,
  RedundancyType,
  StoragePolicyFailureDomain,
  MpfsNode
} from '@schema';
import {
  STATE_ICON_STOP,
  STATUS_ICON_CHECK_O,
  STATUS_ICON_LOADING,
  STATUS_ICON_TIMES_O,
  STATUS_ICON_INACTIVE,
  STATUS_ICON_MAINTENANCE
} from '@/common/utils/statuses';
import computedLocale from '@/common/utils/computedLocale';
import i18n from '@/common/i18n';

export const MIN_SHARE_SIZE = 8;
export const MAX_SHARE_SIZE = 10485760 * 3;
export const DEFAULT_FAILURE_DOMAIN = StoragePolicyFailureDomain.HOST;
export const DEFAULT_REDUNDANCY_TYPE = RedundancyType.RAID6;

export type MpfsShareStatuses = MpfsShare.status;

export const FILE_STORAGE_NODES_STATUS_MAP = computedLocale<Record<MpfsNode.status, { icon: string; text: string }>>(() => ({
  [MpfsNode.status.RUNNING]: {
    icon: STATUS_ICON_CHECK_O,
    text: i18n.t('Healthy'),
  },
  [MpfsNode.status.OFFLINE]: {
    icon: STATUS_ICON_INACTIVE,
    text: i18n.t('Offline'),
  },
  [MpfsNode.status.MAINTENANCE]: {
    icon: STATUS_ICON_MAINTENANCE,
    text: i18n.t('Maintenance'),
  },
}));

export const FILE_STORAGE_NODES_STATUS_FILTERS = computedLocale<{ title: string; value: MpfsNode.status }[]>(() => [
  {
    title: i18n.t('Healthy'),
    value: MpfsNode.status.RUNNING
  },
  {
    title: i18n.t('Offline'),
    value: MpfsNode.status.OFFLINE
  },
  {
    title: i18n.t('Maintenance'),
    value: MpfsNode.status.MAINTENANCE
  }
]);

export const FILE_STORAGE_SHARES_STATUS_ICON_MAP: Record<MpfsShareStatuses, string> = {
  [MpfsShare.status.CREATING]: STATUS_ICON_LOADING,
  [MpfsShare.status.STARTING]: STATUS_ICON_LOADING,
  [MpfsShare.status.STOPPING]: STATUS_ICON_LOADING,
  [MpfsShare.status.DELETING]: STATUS_ICON_LOADING,
  [MpfsShare.status.UPDATING]: STATUS_ICON_LOADING,
  [MpfsShare.status.RUNNING]: STATUS_ICON_CHECK_O,
  [MpfsShare.status.STOPPED]: STATE_ICON_STOP,
  [MpfsShare.status.FAILED]: STATUS_ICON_TIMES_O,
  [MpfsShare.status.MIXED]: STATUS_ICON_TIMES_O,
};

export const FILE_STORAGE_SHARES_STATUS_TEXT = computedLocale<Record<MpfsShareStatuses, string>>(() => ({
  [MpfsShare.status.STARTING]: i18n.t('Starting'),
  [MpfsShare.status.STOPPING]: i18n.t('Stopping'),
  [MpfsShare.status.RUNNING]: i18n.t('Active'),
  [MpfsShare.status.STOPPED]: i18n.t('Stopped'),
  [MpfsShare.status.FAILED]: i18n.t('Failed'),
  [MpfsShare.status.CREATING]: i18n.t('Creating'),
  [MpfsShare.status.DELETING]: i18n.t('Deleting'),
  [MpfsShare.status.UPDATING]: i18n.t('Updating'),
  [MpfsShare.status.MIXED]: i18n.t('Mixed'),
}));

export enum FileStorageExportKeys {
  ENABLED = 'enabled',
  DISABLED = 'disabled',
}

export const FILE_STORAGE_EXPORT_STATUS_TEXT = computedLocale<Record<FileStorageExportKeys, string>>(() => ({
  [FileStorageExportKeys.ENABLED]: i18n.t('Enabled'),
  [FileStorageExportKeys.DISABLED]: i18n.t('Disabled'),
}));

export const FILE_STORAGE_EXPORT_STATUS_ICON_MAP: Record<FileStorageExportKeys, string> = {
  [FileStorageExportKeys.ENABLED]: STATUS_ICON_CHECK_O,
  [FileStorageExportKeys.DISABLED]: STATUS_ICON_TIMES_O,
};
